'use es6';

import I18n from 'I18n';
import { Set as ImmutableSet } from 'immutable';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Table } from 'foundations-components/transitional/components/Table';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { hasSufficientReportEditPermission } from 'reporting-action-components/lib/report-logic';
import { getDashboards } from '../actions/dashboardsActions';
import { updateReport, setReportsFilters, clearReportsFilters, getReportCounts, getReports } from '../actions/reportsActions';
import ReportsTableHeader from '../components/reportsTable/ReportsTableHeader';
import ReportsTableRow from '../components/reportsTable/ReportsTableRow';
import requestStatusType from '../constants/requestStatusTypes';
import { trackEvent, EVENTS } from '../lib/usageTracker';
import { selectFavoriteReports } from '../redux/favorites';
import { selectVisibleColumns } from '../lib/selectVisibleColumns';
import { useSelectedRows } from '../redux/selectedRows';
import { selectFilters } from '../selectors/reportsSelector';
import { selectTableData, selectTableDataStatus } from '../selectors/tableData';
import { ReportsPaginator } from '../components/reportsView/ReportsPaginator';
import { getIsUngatedToReportListViewToggle } from '../selectors/gatesSelector';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const {
  SUCCEEDED,
  FAILED
} = requestStatusType;
const PREVIEW_LIMIT_OVERRIDE = 3;
const ReportsTable = ({
  isBusinessUnitsLoading,
  showPreview = false
}) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters).toJS();
  const {
    sortColumn,
    sortDirection
  } = filters;
  let tableData = useSelector(selectTableData);
  if (showPreview) {
    tableData = tableData.slice(0, PREVIEW_LIMIT_OVERRIDE);
  }
  const actionableRows = ImmutableSet(tableData.filter(report => hasSufficientReportEditPermission(report)));
  const isUngatedToReportListViewToggle = useSelector(getIsUngatedToReportListViewToggle);
  const {
    selectedRows,
    selectRow,
    deselectRow,
    selectedEverything
  } = useSelectedRows({
    actionableRows
  });
  const favoriteReports = useSelector(selectFavoriteReports);
  const tableDataStatus = useSelector(selectTableDataStatus);
  const visibleColumns = useSelector(selectVisibleColumns);
  const numColumns = visibleColumns.length;
  const handleSelectedChange = (report, isSelected) => {
    if (isSelected) {
      selectRow(report);
    } else {
      deselectRow(report);
    }
  };
  const onSortChange = (column, direction) => {
    trackEvent(EVENTS.sortReports, {
      sortColumn: column,
      sortDirection: direction
    });
    dispatch(setReportsFilters(Object.assign({}, filters, {
      sortColumn: column,
      sortDirection: direction,
      offset: 0
    })));
  };
  const TableBodyFailure = /*#__PURE__*/_jsx(NavMarker, {
    name: "REPORTS_LIST_CONTAINER_FAILURE",
    children: /*#__PURE__*/_jsx("tbody", {
      children: /*#__PURE__*/_jsx("tr", {
        children: /*#__PURE__*/_jsx("td", {
          colSpan: numColumns,
          children: /*#__PURE__*/_jsx(Alert, {
            type: "danger",
            titleText: I18n.text('reports.table.failedToLoad.title'),
            closeable: false,
            children: I18n.text('reports.table.failedToLoad.description')
          })
        })
      })
    })
  });
  const TableBodyLoading = /*#__PURE__*/_jsx("tbody", {
    children: /*#__PURE__*/_jsx("tr", {
      children: /*#__PURE__*/_jsx("td", {
        colSpan: numColumns,
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          "data-test-id": "reports-table-loader",
          grow: true
        })
      })
    })
  });
  const TableBodyEmpty = /*#__PURE__*/_jsx(NavMarker, {
    name: "REPORTS_LIST_CONTAINER",
    children: /*#__PURE__*/_jsx("tbody", {
      children: /*#__PURE__*/_jsx("tr", {
        children: /*#__PURE__*/_jsx("td", {
          "data-test-id": "reports-table-empty",
          colSpan: numColumns,
          children: I18n.text('reports.table.empty')
        })
      })
    })
  });
  const TableBodySuccess = /*#__PURE__*/_jsx(NavMarker, {
    name: "REPORTS_LIST_CONTAINER",
    children: /*#__PURE__*/_jsx("tbody", {
      children: tableData.map(report => /*#__PURE__*/_jsx(ReportsTableRow, {
        report: report,
        isSelected: selectedRows.includes(report),
        onSelectedChange: handleSelectedChange,
        onClone: () => {
          dispatch(getDashboards());
          dispatch(clearReportsFilters());
          dispatch(getReportCounts());
        },
        onUpdate: updateReport,
        onAddToDashboard: getReports,
        favoriteReports: favoriteReports,
        selectedEverything: selectedEverything
      }, report.get('id')))
    })
  });
  const TableBody = tableDataStatus === FAILED ? TableBodyFailure : tableDataStatus !== SUCCEEDED || isBusinessUnitsLoading ? TableBodyLoading : tableData.isEmpty() ? TableBodyEmpty : TableBodySuccess;
  const TableColGroup = /*#__PURE__*/_jsx("colgroup", {
    children: visibleColumns.map(columnData => /*#__PURE__*/_jsx("col", {
      style: {
        width: columnData.width
      }
    }, `${columnData.key}-col`))
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Table, {
      condensed: true,
      tableLayout: "fixed",
      children: [TableColGroup, /*#__PURE__*/_jsx(ReportsTableHeader, {
        actionableRows: actionableRows,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        onSortChange: onSortChange
      }), TableBody]
    }), !showPreview && !isUngatedToReportListViewToggle && /*#__PURE__*/_jsx(ReportsPaginator, {})]
  });
};
ReportsTable.propTypes = {
  isBusinessUnitsLoading: PropTypes.bool,
  showPreview: PropTypes.bool
};
export default ReportsTable;